import { createContext, useContext, useState, useCallback, useEffect } from "react";
import type { ReactNode } from "react";
import { useProject } from "./project-context";

interface Branch {
  id: string;
  name: string;
  slug: string;
  status: string;
}

interface BranchContextType {
  activeBranch: string | null;
  activeBranchName: string | null;
  branches: Branch[];
  switchBranch: (slug: string | null) => void;
  refreshBranches: () => void;
}

const BranchContext = createContext<BranchContextType>({
  activeBranch: null,
  activeBranchName: null,
  branches: [],
  switchBranch: () => {},
  refreshBranches: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export function useBranch() {
  return useContext(BranchContext);
}

/**
 * Returns headers to include in fetch calls for branch-aware routes.
 * When no branch is active (main), returns an empty object.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useBranchHeaders(): Record<string, string> {
  const { activeBranch } = useBranch();
  if (!activeBranch) return {};
  return { "X-Branch": activeBranch };
}

export function BranchProvider({ children }: { children: ReactNode }) {
  const { activeProjectId } = useProject();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [activeBranch, setActiveBranch] = useState<string | null>(null);

  const fetchBranches = useCallback(() => {
    if (!activeProjectId) {
      setBranches([]);
      return;
    }
    fetch("/api/branches/")
      .then((r) => r.json())
      .then((d) => setBranches(d.branches || []))
      .catch(() => setBranches([]));
  }, [activeProjectId]);

  // Fetch branches when project changes; reset active branch
  useEffect(() => {
    setActiveBranch(null);
    fetchBranches();
  }, [fetchBranches]);

  const activeBranchName = activeBranch
    ? branches.find((b) => b.slug === activeBranch)?.name || activeBranch
    : null;

  const switchBranch = useCallback((slug: string | null) => {
    setActiveBranch(slug);
  }, []);

  return (
    <BranchContext.Provider
      value={{
        activeBranch,
        activeBranchName,
        branches,
        switchBranch,
        refreshBranches: fetchBranches,
      }}
    >
      {children}
    </BranchContext.Provider>
  );
}
